import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { formatMoney } from '@/shared/lib/money';
import {
  listActiveCotizableArticulos,
  parseArticuloNumber,
} from '@/shared/api/controllers/articulos';
import {
  calcStockCotizable,
  listActiveInventario,
} from '@/shared/api/controllers/inventario';
import { ApiError } from '@/shared/api/client';
import { cambiarHorno, type MantenimientoDetalleRow } from '../api';

/**
 * Cambiar horno — request to swap one horno on a Pendiente-Despacho venta
 * for a different, equal-or-more-expensive one (the client changed their
 * mind after the venta's items can no longer be edited). This only FILES
 * an approval request; an admin resolves it from Aprobaciones, and on
 * approve the line is repriced, inventory moves, and the venta drops to
 * Pago Parcial when a balance is owed.
 *
 * The action is per-horno-line: when the venta has several hornos the user
 * picks which one to replace. The replacement picker only lists hornos
 * whose tax-inclusive price is ≥ the current horno line's total — the same
 * "equal or more expensive" rule the server enforces (on what the customer
 * owes, IVA included).
 */
export function CambiarHornoDialog({
  ventaId,
  moneda,
  porcentajeFactura,
  detalles,
  clienteExterior = false,
  onClose,
}: {
  ventaId: string;
  moneda: 'ARS' | 'MXN' | 'USD';
  /**
   * Venta's facturación % (100 for AR; MX can be <100). Weights the IVA
   * portion of what the customer actually OWES — the same basis the server
   * guard uses (lineOwedContribution). At 100% owed == sticker.
   */
  porcentajeFactura: number;
  detalles: MantenimientoDetalleRow[];
  /** Venta a un cliente del exterior (Factura E): la línea nueva queda a 0% aunque el horno tenga IVA. */
  clienteExterior?: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isUsd = moneda === 'USD';
  const fraction = Math.max(0, Math.min(100, porcentajeFactura)) / 100;
  const parcialFactura = fraction < 1;
  // A line's contribution to what the customer owes. Mirrors the server's
  // lineOwedContribution: IVA weighted by the facturación %.
  const owedOf = (subtotalDescontado: number, ivaRate: number) =>
    Math.round(subtotalDescontado * (1 + (ivaRate / 100) * fraction) * 100) / 100;

  // Horno lines eligible for a swap: serialized (cuenta contable says so)
  // single-unit lines. The server re-validates TipoProducto='Hornos'.
  const hornoLines = useMemo(
    () => detalles.filter((d) => d.llevaNroSerie && d.cantidad === 1),
    [detalles],
  );
  const [lineId, setLineId] = useState<string>(() =>
    hornoLines.length === 1 ? hornoLines[0].id : '',
  );
  const selectedLine = hornoLines.find((l) => l.id === lineId) ?? null;

  const [newArticleId, setNewArticleId] = useState<string>('');
  // Discount % for the new horno. Defaults to the current horno's discount
  // so the client's existing deal is preserved by default — editable.
  const [descuentoRaw, setDescuentoRaw] = useState<string>(() =>
    hornoLines.length === 1 && hornoLines[0].descuento > 0
      ? String(hornoLines[0].descuento)
      : '',
  );
  const [motivo, setMotivo] = useState('');

  const hornosQ = useQuery({
    queryKey: ['articulos', 'cotizables'],
    queryFn: listActiveCotizableArticulos,
    staleTime: 5 * 60_000,
  });
  // Stock pool per article = Disponible + Por Ingresar (calcStockCotizable) —
  // the exact pool the server's approve handler greedy-consumes from, so a
  // candidate with 0 here is the one it rejects with `stock_insuficiente`.
  const inventarioQ = useQuery({
    queryKey: ['inventario', 'activo'],
    queryFn: listActiveInventario,
    staleTime: 60_000,
  });
  const stockByArticulo = useMemo(() => {
    const m = new Map<string, number>();
    for (const inv of inventarioQ.data ?? []) {
      const aid = inv.fields.IDArticulo_I;
      if (aid) m.set(aid, calcStockCotizable(inv));
    }
    return m;
  }, [inventarioQ.data]);

  // What the current horno line contributes to what's owed (0 when no line).
  const oldOwed = selectedLine ? owedOf(selectedLine.subtotalDescontado, selectedLine.iva) : 0;

  // Candidate replacements: active hornos whose OWED contribution (at list
  // price, no discount) ≥ the current line's owed, excluding the line's own
  // article. Cheapest first. The guard is owed-based to match the server;
  // the displayed prices stay sticker (the horno's invoice price).
  const candidates = useMemo(() => {
    if (!selectedLine) return [];
    // Exclude the current horno by ARTICLE ID, not name: two hornos can share
    // a name, and a renamed article would otherwise reappear as a bogus
    // "replacement" that the server rejects with same_article. (Audit 2026-07-16.)
    const currentArticleId = selectedLine.articuloId;
    return (hornosQ.data ?? [])
      .filter(
        (a) =>
          (a.fields.TipoProducto_ART ?? '') === 'Hornos' &&
          (a.fields.Status_ART ?? '') === 'Activo' &&
          a.id !== currentArticleId,
      )
      .map((a) => {
        const unit =
          parseArticuloNumber(
            isUsd ? a.fields.ValorUnDolar_ART : a.fields.ValorUn_ART,
          ) ?? 0;
        const ivaRate = clienteExterior ? 0 : (parseArticuloNumber(a.fields.IVA_ART) ?? 0);
        return {
          id: a.id,
          nombre: a.fields.Articulo_ART ?? '—',
          subtotal: unit,
          ivaRate,
          totalConIva: Math.round(unit * (1 + ivaRate / 100) * 100) / 100,
          stock: stockByArticulo.get(a.id) ?? 0,
        };
      })
      .filter((c) => owedOf(c.subtotal, c.ivaRate) + 0.01 >= oldOwed)
      // In-stock first (A→Z by price), no-stock sink to the bottom disabled.
      .sort((a, b) => {
        const aOut = a.stock > 0 ? 0 : 1;
        const bOut = b.stock > 0 ? 0 : 1;
        if (aOut !== bOut) return aOut - bOut;
        return a.totalConIva - b.totalConIva;
      });
    // owedOf/oldOwed depend on fraction + selectedLine (both in deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hornosQ.data, selectedLine, isUsd, fraction, oldOwed, stockByArticulo, clienteExterior]);

  const selectedCandidate = candidates.find((c) => c.id === newArticleId) ?? null;

  const descuentoPct = Math.max(
    0,
    Math.min(100, parseArticuloNumber(descuentoRaw) ?? 0),
  );
  const newSubDescontado =
    selectedCandidate != null
      ? Math.round(selectedCandidate.subtotal * (1 - descuentoPct / 100) * 100) / 100
      : null;
  // Sticker (invoice-side) new price with the chosen discount — for display.
  const newPriceConIva =
    selectedCandidate != null
      ? Math.round(selectedCandidate.totalConIva * (1 - descuentoPct / 100) * 100) / 100
      : null;
  // What the customer actually pays extra = the OWED difference (IVA weighted
  // by %factura). At 100% this equals the sticker difference.
  const newOwed =
    selectedCandidate != null && newSubDescontado != null
      ? owedOf(newSubDescontado, selectedCandidate.ivaRate)
      : null;
  const diferencia =
    selectedLine && newOwed != null ? Math.round((newOwed - oldOwed) * 100) / 100 : null;
  // Whole-venta invoice total (con IVA) after the swap — sticker, only shown
  // when the venta has more than the horno being swapped.
  const multiArticulo = detalles.length > 1;
  const ventaTotalActual = useMemo(
    () => detalles.reduce((s, d) => s + d.total, 0),
    [detalles],
  );
  const ventaTotalNuevo =
    selectedLine && newPriceConIva != null
      ? Math.round((ventaTotalActual - selectedLine.total + newPriceConIva) * 100) / 100
      : null;
  // The new (discounted) owed must stay ≥ the current line's owed — otherwise
  // the venta would end up overpaid (the flow only collects a positive diff).
  // Server enforces the same owed-based rule.
  const descuentoDemasiadoAlto =
    selectedLine != null && newOwed != null && newOwed + 0.01 < oldOwed;

  const mutation = useMutation({
    mutationFn: () =>
      cambiarHorno(ventaId, {
        lineId,
        newArticleId,
        descuentoPct: descuentoPct > 0 ? descuentoPct : undefined,
        motivo: motivo.trim() || undefined,
      }),
    onSuccess: (r) => {
      if (r.outcome === 'pending_approval_created') {
        toast.success('Cambio de horno enviado a aprobación', {
          description: 'Un administrador debe aprobarlo para que se aplique.',
        });
        qc.invalidateQueries({ queryKey: ['mantenimiento'] });
        qc.invalidateQueries({ queryKey: ['aprobaciones'] });
        onClose();
      } else {
        toast.warning('No se pudo pedir el cambio', { description: r.message });
      }
    },
    onError: (err) =>
      toast.error('No se pudo pedir el cambio de horno', {
        description:
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Error desconocido',
      }),
  });

  const canSubmit =
    Boolean(lineId) &&
    Boolean(newArticleId) &&
    !descuentoDemasiadoAlto &&
    !mutation.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-lg"
        processing={mutation.isPending && 'Enviando a aprobación…'}
      >
        <DialogHeader>
          <DialogTitle>Cambiar horno</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {hornoLines.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Esta venta no tiene hornos que se puedan cambiar.
            </p>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="ch-linea">Horno a reemplazar</Label>
                <Select
                  value={lineId}
                  onValueChange={(v) => {
                    setLineId(v);
                    setNewArticleId('');
                    const l = hornoLines.find((h) => h.id === v);
                    setDescuentoRaw(l && l.descuento > 0 ? String(l.descuento) : '');
                  }}
                  disabled={hornoLines.length === 1}
                >
                  <SelectTrigger id="ch-linea" variant="filled">
                    <SelectValue placeholder="Seleccionar horno…" />
                  </SelectTrigger>
                  <SelectContent>
                    {hornoLines.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.articulo} · {formatMoney(l.total, moneda)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedLine && (
                  <p className="text-xs text-muted-foreground">
                    Los precios incluyen IVA. El reemplazo debe costar igual o más.
                  </p>
                )}
                {selectedLine && selectedLine.descuento > 0 && (
                  <p className="text-xs font-medium text-amber-700">
                    Este horno tiene {selectedLine.descuento}% de descuento aplicado (su
                    precio ya lo refleja).
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ch-nuevo">Nuevo horno</Label>
                <Select
                  value={newArticleId}
                  onValueChange={setNewArticleId}
                  disabled={!selectedLine || hornosQ.isPending || candidates.length === 0}
                >
                  <SelectTrigger id="ch-nuevo" variant="filled">
                    <SelectValue
                      placeholder={
                        hornosQ.isPending
                          ? 'Cargando hornos…'
                          : !selectedLine
                            ? 'Elegí el horno actual'
                            : candidates.length === 0
                              ? 'No hay hornos iguales o más caros'
                              : 'Seleccionar…'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {candidates.map((c) => (
                      <SelectItem key={c.id} value={c.id} disabled={c.stock <= 0}>
                        {c.nombre} · {formatMoney(c.totalConIva, moneda)} ·{' '}
                        {c.stock > 0 ? `Stock: ${c.stock}` : 'Sin stock'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedCandidate && (
                <div className="space-y-1.5">
                  <Label htmlFor="ch-descuento">Descuento del nuevo horno (%)</Label>
                  <Input
                    id="ch-descuento"
                    variant="filled"
                    inputMode="decimal"
                    placeholder="0"
                    value={descuentoRaw}
                    onChange={(e) =>
                      setDescuentoRaw(e.target.value.replace(/[^\d.,]/g, ''))
                    }
                  />
                  {selectedLine && selectedLine.descuento > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Cargado por defecto desde el descuento del horno actual. Ajustalo si
                      corresponde.
                    </p>
                  )}
                </div>
              )}

              {selectedLine && selectedCandidate && diferencia != null && (
                <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-muted-foreground">
                      Precio actual
                      {selectedLine.descuento > 0
                        ? ` (con ${selectedLine.descuento}% desc.)`
                        : ''}
                    </span>
                    <span className="tabular-nums">
                      {formatMoney(selectedLine.total, moneda)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-muted-foreground">
                      Precio nuevo{descuentoPct > 0 ? ` (con ${descuentoPct}% desc.)` : ''}
                    </span>
                    <span className="tabular-nums font-medium">
                      {formatMoney(newPriceConIva ?? 0, moneda)}
                    </span>
                  </div>
                  <div className="mt-1 flex items-baseline justify-between gap-3 border-t pt-1">
                    <span className="text-muted-foreground">Diferencia a cobrar</span>
                    <span
                      className={
                        diferencia >= 0
                          ? 'tabular-nums font-semibold text-emerald-700'
                          : 'tabular-nums font-semibold text-destructive'
                      }
                    >
                      {diferencia >= 0 ? '+' : ''}
                      {formatMoney(diferencia, moneda)}
                    </span>
                  </div>
                  {multiArticulo && ventaTotalNuevo != null && (
                    <div className="mt-1 flex items-baseline justify-between gap-3 border-t pt-1">
                      <span className="text-muted-foreground">
                        Total de la venta (con IVA)
                      </span>
                      <span className="tabular-nums font-medium">
                        {formatMoney(ventaTotalNuevo, moneda)}
                      </span>
                    </div>
                  )}
                  {parcialFactura && (
                    <p className="mt-1.5 border-t pt-1.5 text-xs text-muted-foreground">
                      La diferencia a cobrar considera el {porcentajeFactura}% de
                      facturación de la venta (el IVA se pondera por ese porcentaje).
                    </p>
                  )}
                  {descuentoDemasiadoAlto && (
                    <p className="mt-1.5 border-t border-destructive/30 pt-1.5 text-xs text-destructive">
                      Con ese descuento el horno nuevo cobra menos que el actual.
                      Bajá el descuento — el reemplazo debe cobrar igual o más.
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="ch-motivo">Motivo (opcional)</Label>
                <Textarea
                  id="ch-motivo"
                  variant="filled"
                  rows={3}
                  placeholder="Por qué se cambia el horno"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                />
              </div>

              <p className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800">
                Al aprobarse, la venta vuelve a <strong>Pago Parcial</strong> si queda un
                saldo por cobrar, se ajustan los totales y el inventario. Necesita la
                aprobación de un administrador.
              </p>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            className="gap-2 bg-foreground text-background hover:bg-foreground/90"
          >
            Enviar a aprobación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
